import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ErrorSnackbar from './ErrorSnackbar';
import { Close as CloseIcon } from '@mui/icons-material';
import { Send as SendIcon } from '@mui/icons-material';
import store from 'storeClient';
import ConsultasClient from 'legacyfrontend/api/consultas';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import {
  listingContainedButtonSx,
  listingOutlinedButtonSx,
} from 'legacyfrontend/hooks/listingMuiButtonSx';
import { selectPaisId } from 'legacyfrontend/redux/selectors/application-selectors';

const ContactFormModal = ({
  consultaPackage,
  onClose,
  title = 'Enviá tu consulta',
  subtitle = null,
  variant = 'package', // 'package' | 'agency' | 'banner'
}) => {
  const { t } = useTranslation(['translations', 'layout']);
  const { background, text } = useListingColors();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const applicationState = store.getState().applicationState;
  const paisId = useSelector(selectPaisId);
  const {
    token,
    applicationId,
    templateData,
    nombre: nombreAgencia,
  } = applicationState;

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    comentarios: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  const occupationMapper = {
    '0100': 1,
    '0200': 2,
    '0300': 3,
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      comentarios: '',
    });
    setFieldErrors({});
    setTouchedFields({});
    setError(null);
  };

  const validateNameField = (value) => {
    if (!value || typeof value !== 'string' || value.trim().length === 0) {
      return 'Este campo es requerido';
    }

    return null;
  };

  const validateEmail = (value) => {
    if (!value || typeof value !== 'string' || value.trim().length === 0) {
      return 'Este campo es requerido';
    }

    const trimmedValue = value.trim();

    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmedValue)) {
      return 'Por favor ingrese un email válido';
    }

    if (trimmedValue.includes('..') || trimmedValue.startsWith('.') || trimmedValue.includes('@.') || trimmedValue.match(/\.@|@\./)) {
      return 'Por favor ingrese un email válido';
    }

    return null;
  };

  const validatePhone = (value) => {
    if (!value || value.trim() === '') {
      return null;
    }

    const digitsOnly = value.replace(/\D/g, '');

    if (!/^[0-9]+$/.test(digitsOnly)) {
      return 'El teléfono solo puede contener números';
    }

    return null;
  };

  const validateComments = (value) => {
    if (!value || typeof value !== 'string' || value.trim().length === 0) {
      return 'Este campo es requerido';
    }

    return null;
  };

  const validateField = useCallback((fieldName, value) => {
    let error = null;

    switch (fieldName) {
      case 'nombre':
        error = validateNameField(value);
        break;
      case 'apellido':
        error = validateNameField(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'telefono':
        error = validatePhone(value);
        break;
      case 'comentarios':
        error = validateComments(value);
        break;
      default:
        break;
    }

    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));

    return error === null;
  }, []);

  const handleClose = () => {
    resetForm();
    setIsSubmitting(false);
    onClose();
  };

  const handleInputChange = (field) => (event) => {
    let { value } = event.target;

    if (field === 'telefono') {
      value = value.replace(/[^0-9]/g, '');
      if (value.length > 20) {
        return;
      }
    }

    const newFormData = {
      ...formData,
      [field]: value,
    };

    setFormData(newFormData);

    if (touchedFields[field]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field) => (event) => {
    setTouchedFields((prev) => ({
      ...prev,
      [field]: true,
    }));
    validateField(field, event.target.value);
  };

  const validateForm = () => {
    setError(null);

    const fieldsToValidate = ['nombre', 'apellido', 'email', 'comentarios'];
    const errors = {};
    const touched = {};

    fieldsToValidate.forEach((field) => {
      touched[field] = true;
    });
    setTouchedFields((prev) => ({
      ...prev,
      ...touched,
    }));

    fieldsToValidate.forEach((field) => {
      let error = null;
      switch (field) {
        case 'nombre':
          error = validateNameField(formData.nombre);
          break;
        case 'apellido':
          error = validateNameField(formData.apellido);
          break;
        case 'email':
          error = validateEmail(formData.email);
          break;
        case 'comentarios':
          error = validateComments(formData.comentarios);
          break;
        default:
          break;
      }

      errors[field] = error;
    });

    setFieldErrors(errors);

    const isValid = Object.values(errors).every((error) => error === null);

    if (!isValid) {
      const firstErrorField = fieldsToValidate.find((field) => errors[field] !== null);
      if (firstErrorField) {
        setTimeout(() => {
          const fieldId = `#${firstErrorField}`;
          const fieldName = `[name="${firstErrorField}"]`;

          let fieldElement = document.querySelector(`${fieldId} input`) ||
            document.querySelector(`${fieldId} textarea`) ||
            document.querySelector(`input${fieldName}`) ||
            document.querySelector(`textarea${fieldName}`) ||
            document.querySelector(fieldId);

          if (!fieldElement) {
            const labels = document.querySelectorAll('label');
            for (const label of labels) {
              if (label.getAttribute('for') === firstErrorField || label.textContent.includes(firstErrorField)) {
                const inputId = label.getAttribute('for');
                if (inputId) {
                  fieldElement = document.querySelector(`#${inputId} input`) ||
                    document.querySelector(`#${inputId} textarea`) ||
                    document.querySelector(`#${inputId}`);
                }
                break;
              }
            }
          }

          if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = fieldElement.querySelector('input') || fieldElement.querySelector('textarea') || fieldElement;
            if (input && typeof input.focus === 'function') {
              input.focus();
            }
          }
        }, 150);
      }
    }

    return isValid;
  };
console.log(consultaPackage);
  const preparePackageData = () => {
    if (!consultaPackage) {
      throw new Error('No hay datos del paquete disponibles');
    }

    let ciudadOrigen = '';
    let ciudadDestino = '';
    let provider = '';
    let numeroPaquete = '';
    let categoriaHabitacionPaquete = '';
    let cantidadPersonasPaquete = '';

    const isInternalPackage = Boolean(consultaPackage.isInternal);

    const resolveExternalProviderName = () => {
      if (
        typeof consultaPackage.provider === 'object' &&
        consultaPackage.provider !== null &&
        consultaPackage.provider.nombre
      ) {
        return consultaPackage.provider.nombre;
      }
      if (typeof consultaPackage.providerName === 'object' && consultaPackage.providerName !== null) {
        return (
          consultaPackage.providerName.nombreFantasia ||
          consultaPackage.providerName.nombre ||
          nombreAgencia
        );
      }
      if (typeof consultaPackage.providerName === 'string') {
        return consultaPackage.providerName;
      }
      return nombreAgencia;
    };

    const resolveInternalProviderName = () => {
      const internalProvider = consultaPackage.provider;
      if (internalProvider && typeof internalProvider === 'object') {
        return internalProvider.nombre || internalProvider.nombreFantasia || nombreAgencia;
      }
      return nombreAgencia;
    };

    if (isInternalPackage) {
      ciudadOrigen = consultaPackage.originCity?.name || '';
      ciudadDestino = consultaPackage.paqueteDestinos?.map(destino => destino?.city?.name).filter(Boolean).join(', ') || '';
      provider = resolveInternalProviderName();
      numeroPaquete = consultaPackage.id?.toString() || '';
      categoriaHabitacionPaquete = 'Consultar';
      cantidadPersonasPaquete = 0;
    } else {
      ciudadOrigen = consultaPackage.packageOrigins?.[0]?.originExporta?.nombreCiudad || '';
      ciudadDestino = consultaPackage.packageDestinations?.[0]?.destinationCity?.nombreCiudad || '';
      provider = resolveExternalProviderName();
      numeroPaquete = consultaPackage.externalId || '';
      categoriaHabitacionPaquete = consultaPackage.hotelInformation?.[0]?.roomType || '';
      cantidadPersonasPaquete =
        occupationMapper[consultaPackage.hotelInformation?.[0]?.occupation] || 1;
    }

    return {
      nombre: formData.nombre,
      apellido: formData.apellido,
      nombreMail: `${formData.nombre} ${formData.apellido}`,
      email: formData.email,
      telefono: formData.telefono,
      comentarios: formData.comentarios,
      paisId,
      provider,
      nombreAgencia,
      tituloPaquete: consultaPackage.title || '',
      numeroPaquete,
      origenPaquete: ciudadOrigen,
      destinoPaquete: ciudadDestino,
      habitacionPaquete: 1,
      categoriaHabitacionPaquete,
      cantidadPersonasPaquete,
      precioTotalPaquete: consultaPackage.total || '',
      tipoConsulta: 'paquete',
      agenciaId: applicationId,
      emailAgencia: templateData?.header?.emailContacto || '',
      link: consultaPackage?.link || '',
    };
  };
  const prepareAgencyData = () => {
    return {
      nombre: formData.nombre,
      apellido: formData.apellido,
      email: formData.email,
      telefono: formData.telefono,
      consulta: formData.comentarios,
      nombreAgencia,
      tipoConsulta: 'oficina',
      emailAgencia: consultaPackage?.email || '',
    };
  };

  const prepareBannerData = () => {
    return {
      nombre: formData.nombre,
      apellido: formData.apellido,
      nombreMail: `${formData.nombre} ${formData.apellido}`,
      email: formData.email,
      telefono: formData.telefono,
      comentarios: formData.comentarios,
      paisId,
      provider: nombreAgencia,
      nombreAgencia,
      tituloPaquete: consultaPackage?.titulo || '',
      bannerId: consultaPackage?.id,
      bannerTitle: consultaPackage?.titulo,
      bannerDescription: consultaPackage?.descripcion,
      tipoConsulta: 'banners',
      agenciaId: applicationId,
      emailAgencia: templateData?.header?.emailContacto || '',
      id: consultaPackage?.id,
      proveedor: consultaPackage?.providerName,
    };
  };

  const enviarConsulta = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      if (!token) {
        setError('Error: No hay token de autenticación disponible');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        let data;
        let response;

        if (variant === 'agency') {
          data = prepareAgencyData();
          response = await ConsultasClient.enviarMailContacto(data, token);
        } else if (variant === 'banner') {
          data = prepareBannerData();
          response = await ConsultasClient.consultaMarcaBlanca(data, token);

          if (response.status === 201) {
            data.id = response.data.id;
            await ConsultasClient.enviarMailConsulta(data, token);
          }
        } else {
          data = preparePackageData();
          response = await ConsultasClient.consultaMarcaBlanca(data, token);

          if (response.status === 201) {
            data.id = response.data.id;
            await ConsultasClient.enviarMailConsulta(data, token);
          }
        }

        if (response.status === 200 || response.status === 201) {
          setShowSuccess(true);
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else {
          setError('La consulta no se pudo enviar');
          setIsSubmitting(false);
        }
      } catch (error) {
        let errorMessage = 'Error al enviar la consulta. Por favor intente nuevamente.';
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.message) {
            errorMessage = Array.isArray(error.response.data.message)
              ? error.response.data.message.join(', ')
              : error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }

        setError(errorMessage);
        setIsSubmitting(false);
      }
    },
    [formData, consultaPackage, variant, token]
  );

  const handleCloseSnackbar = () => {
    setShowSuccess(false);
  };

  return (
    <>
      <Dialog
        open={!!consultaPackage}
        onClose={handleClose}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}
        disableScrollLock={false}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '100dvh',
          },
        }}
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        <DialogTitle
          sx={{
            paddingBottom: 0,
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box
              display='flex'
              alignItems='center'
              gap={1}
              sx={{ width: '100%', position: 'relative' }}
            >
              <SendIcon style={{ color: background }} />
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              <IconButton
                onClick={handleClose}
                size='small'
                sx={{ position: 'absolute', right: 0 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {subtitle && (
            <Box mt={1} color='text.secondary' fontSize='1.1rem' pt={2}>
              {subtitle}
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ overflowY: 'auto' }}>
          <Box component='form' onSubmit={enviarConsulta} noValidate>
            <TextField
              fullWidth
              name='nombre'
              id='nombre'
              label={t('requestForm.name')}
              value={formData.nombre}
              onChange={handleInputChange('nombre')}
              onBlur={handleBlur('nombre')}
              margin='normal'
              required
              disabled={isSubmitting}
              error={touchedFields.nombre && !!fieldErrors.nombre}
              helperText={touchedFields.nombre ? fieldErrors.nombre : ''}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              name='apellido'
              id='apellido'
              label={t('requestForm.lastName')}
              value={formData.apellido}
              onChange={handleInputChange('apellido')}
              onBlur={handleBlur('apellido')}
              margin='normal'
              required
              disabled={isSubmitting}
              error={touchedFields.apellido && !!fieldErrors.apellido}
              helperText={touchedFields.apellido ? fieldErrors.apellido : ''}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              name='email'
              id='email'
              label='E-mail'
              type='email'
              value={formData.email}
              onChange={handleInputChange('email')}
              onBlur={handleBlur('email')}
              margin='normal'
              required
              disabled={isSubmitting}
              error={touchedFields.email && !!fieldErrors.email}
              helperText={touchedFields.email ? fieldErrors.email : ''}
              inputProps={{ maxLength: 100 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              name='telefono'
              id='telefono'
              label={t('requestForm.phone')}
              type='tel'
              value={formData.telefono}
              onChange={handleInputChange('telefono')}
              onBlur={handleBlur('telefono')}
              margin='normal'
              disabled={isSubmitting}
              error={touchedFields.telefono && !!fieldErrors.telefono}
              helperText={touchedFields.telefono ? fieldErrors.telefono : ''}
              inputProps={{
                maxLength: 20,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              name='comentarios'
              id='comentarios'
              label={t('requestForm.message')}
              multiline
              rows={4}
              value={formData.comentarios}
              onChange={handleInputChange('comentarios')}
              onBlur={handleBlur('comentarios')}
              margin='normal'
              required
              disabled={isSubmitting}
              error={touchedFields.comentarios && !!fieldErrors.comentarios}
              helperText={touchedFields.comentarios ? fieldErrors.comentarios : ''}
              inputProps={{ maxLength: 1000 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            {error && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          <Button
            disabled={isSubmitting}
            onClick={handleClose}
            sx={listingOutlinedButtonSx(background, {
              '&:disabled': {
                color: background,
                borderColor: background,
                opacity: 0.5,
              },
            })}
            variant='outlined'
          >
            Cancelar
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={enviarConsulta}
            startIcon={<SendIcon sx={{ color: 'inherit' }} />}
            sx={listingContainedButtonSx(background, text)}
            variant='contained'
          >
            {isSubmitting ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorSnackbar
        open={showSuccess}
        message='La consulta se envió correctamente'
        onClose={handleCloseSnackbar}
        severity='info'
      />
    </>
  );
};

export default ContactFormModal;
